import React, { useState, useEffect } from 'react';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';
import { observer } from 'mobx-react-lite';
import { useOnboardingViewModel } from '../../view-model/onboarding/onboarding-view-model';
import { SECTION_DASHBOARD, SECTION_CALENDAR, SECTION_RESERVATION, SECTION_AVAILABILITY, SECTION_SERVICES, SECTION_PROFILE } from '../../constants/onboarding-sections';
import professionalStore from '../../store/professional-store';
import { t } from 'i18next';

// Types pour les propriétés du composant
interface OnboardingTourProps {
  section: string;
  autoStart?: boolean;
}

// Définition des étapes par section
const sectionSteps: Record<string, Step[]> = {
  [SECTION_DASHBOARD]: [
    {
      target: '.dashboard-reservations-left',
      content: t('onboarding.dashboard.left'),
      disableBeacon: true,
      placement: 'right',
    },
    {
      target: '.dashboard-new-appointment',
      content: t('onboarding.dashboard.right'),
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '.dashboard-availability',
      content: t('onboarding.dashboard.bottom'),
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '.dashboard-revenue',
      content: t('onboarding.dashboard.revenue'),
      disableBeacon: true,
      placement: 'right',
    },
  ],
  [SECTION_CALENDAR]: [
    {
      target: '.calendar',
      content: t('onboarding.calendar.left'),
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '.calendar-appointment',
      content: t('onboarding.calendar.right'),
      disableBeacon: true,
      placement: 'bottom',
    },
  ],
  [SECTION_RESERVATION]: [
    {
      target: '.reservation-view',
      content: t('onboarding.reservation.left'),
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '.reservation-filter-week',
      content: t('onboarding.reservation.filterWeek'),
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '.reservation-filter-status',
      content: t('onboarding.reservation.filterStatus'),
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '.reservation-stats',
      content: t('onboarding.reservation.stats'),
      disableBeacon: true,
      placement: 'bottom',
    },
  ],
  [SECTION_SERVICES]: [
    {
      target: '.services-filter-status',
      content: t('onboarding.services.filterStatus'),
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '.services-filter-search',
      content: t('onboarding.services.filterSearch'),
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '.services-view',
      content: t('onboarding.services.view'),
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '.services-add',
      content: t('onboarding.services.add'),
      disableBeacon: true,
      placement: 'bottom',
    },
  ],
  [SECTION_PROFILE]: [
    {
      target: '.profile-update',
      content: t('onboarding.profile.update'),
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '.profile-personal-info',
      content: t('onboarding.profile.personalInfo'),
      disableBeacon: true,
      placement: 'left',
    },
  ],
  [SECTION_AVAILABILITY]: [
    {
      target: '.availability-timezone',
      content: t('onboarding.availability.timezone'),
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '.availability-link',
      content: t('onboarding.availability.link'),
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '.availability-add',
      content: t('onboarding.availability.add'),
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '.availability-view',
      content: t('onboarding.availability.view'),
      disableBeacon: true,
      placement: 'top',
    },
    {
      target: '.availability-navigate',
      content: t('onboarding.availability.navigate'),
      disableBeacon: true,
      placement: 'bottom',
    },
  ],
};

const OnboardingTour: React.FC<OnboardingTourProps> = observer(({ section, autoStart = false }) => {
  const onboardingViewModel = useOnboardingViewModel();
  const [run, setRun] = useState(false);
  const [currentStep, setCurrentStep] = useState<number | null>(null);
  
  // Obtenir les étapes pour cette section
  const steps: Step[] = sectionSteps[section] || [];

  // Fonction pour mettre à jour l'étape d'onboarding dans le ViewModel
  const updateSectionStep = (step: number | null) => {
    onboardingViewModel.updateSectionStep(section, step);
  };

  // Fonction pour terminer l'onboarding
  const finishOnboarding = () => {
    setRun(false);
    updateSectionStep(null);
    onboardingViewModel.updateSectionOnboarding(section, true);
  };

  // Vérifier si le profil est chargé
  const profileLoaded = !!professionalStore.profileProfessional;

  useEffect(() => {
    if (!profileLoaded) return;
  
    onboardingViewModel.initializeOnboardingStatus();
  
    if (!autoStart) {
      const globalOnboardingCompleted = onboardingViewModel.globalOnboardingCompleted;
      if (globalOnboardingCompleted) return;
    }
  
    const sectionCompleted = onboardingViewModel.getSectionOnboardingStatus(section);
  
    if (!sectionCompleted || autoStart) {
      setRun(true);
  
      const savedStep = onboardingViewModel.getCurrentOnboardingStep(section);
      if (savedStep !== null) {
        setCurrentStep(savedStep);
      } else {
        setCurrentStep(0);
        updateSectionStep(0);
      }
    }
  }, [profileLoaded, onboardingViewModel, section, autoStart]);
  

  useEffect(() => {
    const handleSkipButton = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target && target.textContent?.includes(t('onboarding.skip'))) {
        setTimeout(() => {
          if (currentStep === 0) {
            finishOnboarding();
          }
        }, 0);
      }
    };
  
    document.addEventListener('click', handleSkipButton);
    
    return () => {
      document.removeEventListener('click', handleSkipButton);
    };
  }, [currentStep]);

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, index, type, action } = data;
    
    // Mettre à jour l'étape actuelle
    if (type === 'step:after' && index !== undefined) {
      const nextStep = index + 1;
      setCurrentStep(nextStep);
      updateSectionStep(nextStep);
    }
    
    // Mettre à jour l'étape actuelle au début du tutoriel
    if (type === 'step:before' && index === 0) {
      setCurrentStep(0);
      updateSectionStep(0);
    }
    
    // FIX : Gérer le skip sur la première étape
    if (action === 'skip' && index === 0) {
      finishOnboarding();
      return;
    }
    
    // Quand le tutoriel est terminé ou fermé
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      finishOnboarding();
    }
  };

  return (
    <Joyride
      callback={handleJoyrideCallback}
      continuous
      hideCloseButton={false}
      run={run}
      scrollToFirstStep
      showProgress
      showSkipButton
      steps={steps}
      // Ajouter des propriétés pour améliorer la gestion
      disableOverlay={false}
      disableScrollParentFix={true}
      // Bouton skip personnalisé si nécessaire
      locale={{
        skip: t('onboarding.skip'),
        next: t('onboarding.next'),
        back: t('onboarding.back'),
        close: t('onboarding.close'),
        last: t('onboarding.last')
      }}
      styles={{
        options: {
          // Couleurs principales de l'application
          primaryColor: '#23A892', // Couleur primaire (indigo)
          backgroundColor: '#ffffff',
          textColor: '#1F2937',
          arrowColor: '#ffffff',
          overlayColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 10000, // S'assurer que le tooltip est au-dessus de tout
        },
        tooltip: {
          borderRadius: '0.75rem', // Coins plus arrondis
          fontSize: '0.875rem',
          padding: '1rem',
          backgroundColor: '#FFFFFFFF',
          color: '#1F2937',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        tooltipTitle: {
          fontSize: '1rem',
          fontWeight: 600,
          color: '#111827',
          marginBottom: '0.5rem',
        },
        tooltipContent: {
          fontSize: '0.875rem',
          lineHeight: '1.5',
        },
        buttonNext: {
          backgroundColor: '#23A892',
          borderRadius: '0.5rem',
          color: '#ffffff',
          fontSize: '0.875rem',
          fontWeight: 500,
          padding: '0.5rem 1rem',
          border: 'none',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
        },
        buttonBack: {
          color: '#23A892',
          fontSize: '0.875rem',
          fontWeight: 500,
          marginRight: '0.75rem',
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '0.5rem 1rem',
        },
        buttonSkip: {
          color: '#6B7280',
          fontSize: '0.875rem',
          fontWeight: 500,
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '0.5rem 1rem',
        },
        buttonClose: {
          color: '#9CA3AF',
          fontSize: '1.25rem',
          fontWeight: 'bold',
        },
        spotlight: {
          borderRadius: '0.5rem',
          boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5), 0 0 15px rgba(0, 0, 0, 0.3)',
        },
      }}
    />
  );
});

export default OnboardingTour;